import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import './IcebergPage.css';

const API_BASE = process.env.REACT_APP_API_URL;

const TIERS = [
  { id: 'event',        label: 'Event',          number: '1', desc: 'Contain it. Document it.', color: '#FC8181', bg: 'rgba(252,129,129,0.06)', border: 'rgba(252,129,129,0.25)' },
  { id: 'pattern',      label: 'Pattern',         number: '2', desc: 'Analyse recurrence. Form hypothesis.', color: '#F6AD55', bg: 'rgba(246,173,85,0.06)', border: 'rgba(246,173,85,0.25)' },
  { id: 'structure',    label: 'System Structure', number: '3', desc: 'Confirm root cause. Implement changes.', color: '#63B3ED', bg: 'rgba(99,179,237,0.06)', border: 'rgba(99,179,237,0.25)' },
  { id: 'mental-model', label: 'Mental Model',     number: '4', desc: 'Prevent recurrence. Verify. Sign off.', color: '#46BB89', bg: 'rgba(70,187,137,0.06)', border: 'rgba(70,187,137,0.25)' },
];

const STATUS_COLORS = { open: '#63B3ED', 'in-progress': '#F6AD55', resolved: '#46BB89', archived: '#9ca3af' };

const AREAS = [
  { code: 'SCM', label: 'Supply Chain Management' }, { code: 'PE',   label: 'Production Engineering' },
  { code: 'PD',  label: 'Product Development' },      { code: 'QA',   label: 'Quality Assurance' },
  { code: 'SRV', label: 'Service / After-sales' },    { code: 'MGT',  label: 'Management' },
  { code: 'PROC',label: 'Procurement' },
];
const AREA_COLORS = { SCM:'#63B3ED', PE:'#F6AD55', PD:'#46BB89', QA:'#FC8181', SRV:'#B794F4', MGT:'#9ca3af', PROC:'#F687B3' };

const RCA_METHODS = [
  { value: '5-why',          label: '5-Why Analysis' },
  { value: 'fishbone',       label: 'Fishbone / Ishikawa' },
  { value: 'fmea',           label: 'FMEA' },
  { value: 'fault-tree',     label: 'Fault Tree Analysis' },
  { value: 'kepner-tregoe',  label: 'Kepner-Tregoe' },
  { value: 'other',          label: 'Other' },
];

const EVIDENCE_TYPES = [
  'photo','report','lab_test','email','drawing','specification','procedure_update','measurement','other',
];
const ECO_STATUSES  = ['draft','submitted','approved','implemented'];
const PCN_STATUSES  = ['draft','sent','acknowledged'];

// ── Tier gate requirements (mirrors backend checkTierGate) ────────────────────
function computeGate(doc) {
  const tier = doc?.tier;
  const errors = [];
  if (tier === 'event') {
    const f = doc.fiveWh || {};
    if (!f.what?.trim())  errors.push('5W1H: describe what specifically failed ("What")');
    if (!f.who?.trim())   errors.push('5W1H: record who detected or is affected ("Who")');
    if (!f.when?.trim())  errors.push('5W1H: record when it occurred ("When")');
    if (!doc.containmentAction?.trim()) errors.push('Immediate containment action required');
    if ((doc.linkedRmaIds?.length || 0) < 1 && (doc.evidence?.length || 0) < 1) {
      errors.push('Attach at least one piece of evidence (RMA link or document)');
    }
  }
  if (tier === 'pattern') {
    if (!doc.rcaMethod) errors.push('Select a root cause analysis method');
    if (!doc.rootCauseHypothesis?.trim()) errors.push('Document the root cause hypothesis');
    if (!doc.patternFrequency?.trim()) errors.push('Describe the pattern frequency / scope');
    const hasAssigned = (doc.actions || []).some(a => a.person?.trim() && a.dueDate);
    if (!hasAssigned) errors.push('At least one corrective action must have an owner and due date');
  }
  if (tier === 'structure') {
    if (!doc.rootCauseConfirmed?.trim()) errors.push('Confirm the root cause');
    const hasDone = (doc.actions || []).some(a => a.status === 'done');
    if (!hasDone) errors.push('At least one corrective action must be completed');
    const hasRef = (doc.ecoRefs?.length || 0) > 0 || (doc.pcnRefs?.length || 0) > 0 || (doc.linkedDocumentNumbers?.length || 0) > 0;
    if (!hasRef) errors.push('Reference at least one change record (ECO, PCN, or procedure)');
  }
  return errors;
}

function computeCloseGate(doc) {
  if (doc?.tier !== 'mental-model') return [];
  const errors = [];
  if (!doc.preventiveAction?.trim()) errors.push('Document the preventive action taken');
  if (!doc.effectivenessVerification?.trim()) errors.push('Define how effectiveness will be verified');
  if (!doc.managementReview?.reviewedBy?.trim()) errors.push('Management review sign-off required');
  return errors;
}

// ── API ───────────────────────────────────────────────────────────────────────
const FETCH_TIMEOUT_MS = 20000;

async function apiFetch(path, options = {}, getToken) {
  const tokenRace = Promise.race([
    getToken(),
    new Promise((_, reject) => setTimeout(() => reject(new Error('Auth timeout — please refresh')), FETCH_TIMEOUT_MS)),
  ]);
  let token;
  try { token = await tokenRace; } catch (err) { throw err; }
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const r = await fetch(`${API_BASE}${path}`, {
      ...options, signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(options.headers || {}) },
    });
    if (!r.ok) {
      let body = {};
      try { body = await r.json(); } catch (_) {}
      const err = new Error(body.error || `API ${r.status}`);
      err.gateErrors = body.gateErrors || null;
      throw err;
    }
    return r.json();
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Server did not respond — it may be starting up, try again');
    throw err;
  } finally { clearTimeout(tid); }
}

// ── Case card ─────────────────────────────────────────────────────────────────
function CaseCard({ doc, tier, getToken, onUpdated, onSelect, selectMode, selected, onToggleSelect }) {
  const [analysing, setAnalysing] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteErr, setPromoteErr] = useState(null);
  const [closing,   setClosing]   = useState(false);

  const handleAnalyse = async (e) => {
    e.stopPropagation();
    setAnalysing(true);
    try { await apiFetch(`/iceberg/${doc._id}/analyse`, { method: 'POST', body: JSON.stringify({}) }, getToken); onUpdated(); }
    catch (_) {} finally { setAnalysing(false); }
  };

  const handlePromote = async (e) => {
    e.stopPropagation();
    setPromoting(true);
    setPromoteErr(null);
    try { await apiFetch(`/iceberg/${doc._id}/promote`, { method: 'POST', body: JSON.stringify({}) }, getToken); onUpdated(); }
    catch (err) { setPromoteErr(err.gateErrors ? err.gateErrors : [err.message]); }
    finally { setPromoting(false); }
  };

  const handleClose = async (e) => {
    e.stopPropagation();
    setClosing(true);
    try { await apiFetch(`/iceberg/${doc._id}`, { method: 'PATCH', body: JSON.stringify({ status: 'resolved', closedReason: 'Closed by user' }) }, getToken); onUpdated(); }
    catch (err) { setPromoteErr(err.gateErrors ? err.gateErrors : [err.message]); }
    finally { setClosing(false); }
  };

  const isEvent    = tier.id === 'event';
  const isLastTier = tier.id === 'mental-model';
  const isResolved = doc.status === 'resolved' || doc.status === 'archived';

  const gateErrors  = computeGate(doc);
  const doneActions = (doc.actions || []).filter(a => a.status === 'done').length;
  const totalActions = (doc.actions || []).length;
  const hasRequirements = (tier.id === 'event' && (!!doc.fiveWh?.what || !!doc.containmentAction)) ||
    (tier.id === 'pattern' && (!!doc.rcaMethod || !!doc.rootCauseHypothesis)) ||
    (tier.id === 'structure' && (!!doc.rootCauseConfirmed)) ||
    (tier.id === 'mental-model' && (!!doc.preventiveAction));

  return (
    <div
      className={`ib-card ${selected ? 'ib-card--selected' : ''}`}
      onClick={() => { if (selectMode && isEvent) { onToggleSelect(doc._id); return; } onSelect(doc); }}
      style={{ borderColor: selected ? tier.color : tier.border }}
    >
      {selectMode && isEvent && (
        <div className="ib-card-select" onClick={e => { e.stopPropagation(); onToggleSelect(doc._id); }}>
          <span className={`ib-checkbox ${selected ? 'ib-checkbox--checked' : ''}`}
            style={{ borderColor: tier.color, background: selected ? tier.color : '' }}>{selected && '✓'}</span>
        </div>
      )}

      {doc.origin === 'ai' && <span className="ib-card-origin">◈ AI detected</span>}
      <div className="ib-card-title">{doc.title}</div>

      {doc.productNumbers?.length > 0 && (
        <div className="ib-card-tags">
          {doc.productNumbers.slice(0, 3).map(pn => (
            <span key={pn} className="ib-card-tag" style={{ color: tier.color, borderColor: `${tier.color}40`, background: `${tier.color}0d` }}>{pn}</span>
          ))}
          {doc.productNumbers.length > 3 && <span className="ib-card-tag-more">+{doc.productNumbers.length - 3}</span>}
        </div>
      )}

      {/* Progress indicators */}
      <div className="ib-card-progress">
        {hasRequirements && (
          <span className="ib-card-progress-item" style={{ color: '#46BB89' }}>
            ◈ Docs
          </span>
        )}
        {doc.evidence?.length > 0 && (
          <span className="ib-card-progress-item" style={{ color: '#63B3ED' }}>
            📎 {doc.evidence.length}
          </span>
        )}
        {totalActions > 0 && (
          <span className="ib-card-progress-item" style={{ color: doneActions === totalActions ? '#46BB89' : '#F6AD55' }}>
            ✓ {doneActions}/{totalActions}
          </span>
        )}
        {(doc.ecoRefs?.length || 0) > 0 && (
          <span className="ib-card-progress-item" style={{ color: '#B794F4' }}>ECO</span>
        )}
        {(doc.pcnRefs?.length || 0) > 0 && (
          <span className="ib-card-progress-item" style={{ color: '#F687B3' }}>PCN</span>
        )}
        {(doc.linkedDocumentNumbers?.length || 0) > 0 && (
          <span className="ib-card-progress-item" style={{ color: '#46BB89' }}>QP</span>
        )}
      </div>

      {/* Gate warning dot */}
      {!isResolved && gateErrors.length > 0 && (
        <div className="ib-card-gate-warn">
          <span className="ib-gate-dot" />
          {gateErrors.length} requirement{gateErrors.length > 1 ? 's' : ''} pending
        </div>
      )}

      {doc.aiAnalysis?.suggestPromotion && !isLastTier && !isEvent && !isResolved && (
        <div className="ib-card-ai-hint">◈ AI suggests advancing to {doc.aiAnalysis.promotionTier || 'next tier'}</div>
      )}

      <div className="ib-card-status">
        <span className="ib-status-dot" style={{ background: STATUS_COLORS[doc.status] || '#9ca3af' }} />
        <span className="ib-status-label">{doc.status}</span>
        <span className="ib-card-date">{new Date(doc.createdAt).toLocaleDateString('en-GB')}</span>
      </div>

      {promoteErr && (
        <div className="ib-card-gate-errors" onClick={e => e.stopPropagation()}>
          {promoteErr.map((e, i) => <div key={i}>○ {e}</div>)}
          <button className="ib-gate-err-close" onClick={() => setPromoteErr(null)}>✕</button>
        </div>
      )}

      {!isResolved && !selectMode && (
        <div className="ib-card-actions" onClick={e => e.stopPropagation()}>
          <button className="ib-btn ib-btn--ai" onClick={handleAnalyse} disabled={analysing}>{analysing ? '…' : '◈ Analyse'}</button>
          {!isLastTier && (!isEvent || (doc.linkedRmaIds?.length || 0) >= 2) && (
            <button className="ib-btn ib-btn--promote" onClick={handlePromote} disabled={promoting}
              style={{ borderColor: tier.color, color: tier.color }}>
              {promoting ? '…' : isEvent ? '↑ Promote' : '↑ Advance'}
            </button>
          )}
          <button className="ib-btn ib-btn--close" onClick={handleClose} disabled={closing}>{closing ? '…' : '✓ Close'}</button>
        </div>
      )}
    </div>
  );
}

// ── Tier 1 panel — 5W1H + containment ────────────────────────────────────────
function Tier1Panel({ doc, getToken, onUpdated }) {
  const f = doc.fiveWh || {};
  const [form, setForm] = useState({
    who: f.who || '', what: f.what || '', when: f.when || '',
    where: f.where || '', why: f.why || '', how: f.how || '',
    containmentAction: doc.containmentAction || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/iceberg/${doc._id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          fiveWh: { who: form.who, what: form.what, when: form.when, where: form.where, why: form.why, how: form.how },
          containmentAction: form.containmentAction,
        }),
      }, getToken);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated();
    } catch (_) {} finally { setSaving(false); }
  };

  const field = (key, label, placeholder, required) => (
    <label className="ib-form-label">
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}
        {required && <span className="ib-req-star">*</span>}
        {form[key]?.trim() && <span className="ib-field-ok">✓</span>}
      </span>
      <input className="ib-form-input" value={form[key]} placeholder={placeholder}
        onChange={e => setForm(d => ({ ...d, [key]: e.target.value }))} />
    </label>
  );

  return (
    <div className="ib-tier-panel">
      <div className="ib-tier-panel-head" style={{ borderLeftColor: '#FC8181' }}>
        <span style={{ color: '#FC8181' }}>D2/D3 — Problem Description &amp; Containment</span>
        <span className="ib-tier-panel-iso">ISO 9001:2015 cl. 10.2.1</span>
      </div>
      <div className="ib-fivewh-grid">
        {field('what',  '①  What failed?',          'Describe the specific failure or nonconformity', true)}
        {field('who',   '②  Who detected / affected?','Who found it? Which customer / product line?',  true)}
        {field('when',  '③  When?',                  'Date detected, date of occurrence, frequency',   true)}
        {field('where', '④  Where?',                 'Process step, location, geography',              false)}
        {field('why',   '⑤  Initial why?',           'First hypothesis — will be refined in Pattern',  false)}
        {field('how',   '⑥  How detected?',          'Customer complaint, audit, test, visual inspection', false)}
      </div>
      <label className="ib-form-label" style={{ marginTop: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Immediate Containment Action (D3) <span className="ib-req-star">*</span>
          {form.containmentAction?.trim() && <span className="ib-field-ok">✓</span>}
        </span>
        <textarea className="ib-modal-textarea" rows={3} value={form.containmentAction}
          placeholder="What was done immediately to prevent further impact? Quarantine, rework, customer notification…"
          onChange={e => setForm(d => ({ ...d, containmentAction: e.target.value }))} />
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="ib-btn ib-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save documentation'}
        </button>
      </div>
    </div>
  );
}

// ── Tier 2 panel — RCA method + pattern scope + hypothesis ────────────────────
function Tier2Panel({ doc, getToken, onUpdated }) {
  const [form, setForm] = useState({
    rcaMethod:           doc.rcaMethod           || '',
    rcaMethodNotes:      doc.rcaMethodNotes       || '',
    patternFrequency:    doc.patternFrequency     || '',
    rootCauseHypothesis: doc.rootCauseHypothesis  || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/iceberg/${doc._id}`, { method: 'PATCH', body: JSON.stringify(form) }, getToken);
      setSaved(true); setTimeout(() => setSaved(false), 2000); onUpdated();
    } catch (_) {} finally { setSaving(false); }
  };

  return (
    <div className="ib-tier-panel">
      <div className="ib-tier-panel-head" style={{ borderLeftColor: '#F6AD55' }}>
        <span style={{ color: '#F6AD55' }}>D4 — Root Cause Analysis (Pattern)</span>
        <span className="ib-tier-panel-iso">ISO 9001:2015 cl. 10.2.1b</span>
      </div>

      <label className="ib-form-label">
        RCA Method <span className="ib-req-star">*</span>
        {form.rcaMethod && <span className="ib-field-ok"> ✓</span>}
        <select className="ib-form-input" value={form.rcaMethod}
          onChange={e => setForm(d => ({ ...d, rcaMethod: e.target.value }))}>
          <option value="">Select method…</option>
          {RCA_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </label>

      <label className="ib-form-label">
        RCA document / diagram link
        <input className="ib-form-input" value={form.rcaMethodNotes}
          placeholder="URL to fishbone diagram, 5-why worksheet, FMEA document…"
          onChange={e => setForm(d => ({ ...d, rcaMethodNotes: e.target.value }))} />
      </label>

      <label className="ib-form-label">
        Pattern frequency / scope <span className="ib-req-star">*</span>
        {form.patternFrequency?.trim() && <span className="ib-field-ok"> ✓</span>}
        <input className="ib-form-input" value={form.patternFrequency}
          placeholder="e.g. 4 occurrences in 3 months, products WE270 / WE240, field and workshop"
          onChange={e => setForm(d => ({ ...d, patternFrequency: e.target.value }))} />
      </label>

      <label className="ib-form-label">
        Root cause hypothesis <span className="ib-req-star">*</span>
        {form.rootCauseHypothesis?.trim() && <span className="ib-field-ok"> ✓</span>}
        <textarea className="ib-modal-textarea" rows={4} value={form.rootCauseHypothesis}
          placeholder="What is the most likely root cause based on the analysis? Reference the RCA tool used."
          onChange={e => setForm(d => ({ ...d, rootCauseHypothesis: e.target.value }))} />
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="ib-btn ib-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save analysis'}
        </button>
      </div>
    </div>
  );
}

// ── Tier 3 panel — confirmed root cause + ECO/PCN/procedure refs ──────────────
function Tier3Panel({ doc, getToken, onUpdated }) {
  const [form, setForm] = useState({
    rootCauseConfirmed:      doc.rootCauseConfirmed      || '',
    correctiveActionSummary: doc.correctiveActionSummary || '',
  });
  const [savingMain, setSavingMain] = useState(false);
  const [savedMain, setSavedMain]   = useState(false);

  // ECO state
  const [newEco, setNewEco]   = useState({ number: '', title: '', status: 'draft', date: '', notes: '' });
  const [addingEco, setAddingEco] = useState(false);
  const [savingEco, setSavingEco] = useState(null);

  // PCN state
  const [newPcn, setNewPcn]   = useState({ number: '', title: '', sentDate: '', customersNotified: '', status: 'draft', notes: '' });
  const [addingPcn, setAddingPcn] = useState(false);
  const [savingPcn, setSavingPcn] = useState(null);

  // Procedure link state
  const [newDocNum, setNewDocNum] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);

  const saveMain = async () => {
    setSavingMain(true);
    try {
      await apiFetch(`/iceberg/${doc._id}`, { method: 'PATCH', body: JSON.stringify(form) }, getToken);
      setSavedMain(true); setTimeout(() => setSavedMain(false), 2000); onUpdated();
    } catch (_) {} finally { setSavingMain(false); }
  };

  const addEco = async () => {
    if (!newEco.number.trim()) return;
    setSavingEco('new');
    try {
      await apiFetch(`/iceberg/${doc._id}/ecos`, { method: 'POST', body: JSON.stringify(newEco) }, getToken);
      setNewEco({ number: '', title: '', status: 'draft', date: '', notes: '' });
      setAddingEco(false); onUpdated();
    } catch (_) {} finally { setSavingEco(null); }
  };

  const patchEco = async (ecoId, patch) => {
    setSavingEco(ecoId);
    try { await apiFetch(`/iceberg/${doc._id}/ecos/${ecoId}`, { method: 'PATCH', body: JSON.stringify(patch) }, getToken); onUpdated(); }
    catch (_) {} finally { setSavingEco(null); }
  };

  const removeEco = async (ecoId) => {
    setSavingEco(ecoId + '-del');
    try { await apiFetch(`/iceberg/${doc._id}/ecos/${ecoId}`, { method: 'DELETE' }, getToken); onUpdated(); }
    catch (_) {} finally { setSavingEco(null); }
  };

  const addPcn = async () => {
    if (!newPcn.number.trim()) return;
    setSavingPcn('new');
    try {
      await apiFetch(`/iceberg/${doc._id}/pcns`, { method: 'POST', body: JSON.stringify(newPcn) }, getToken);
      setNewPcn({ number: '', title: '', sentDate: '', customersNotified: '', status: 'draft', notes: '' });
      setAddingPcn(false); onUpdated();
    } catch (_) {} finally { setSavingPcn(null); }
  };

  const removePcn = async (pcnId) => {
    setSavingPcn(pcnId + '-del');
    try { await apiFetch(`/iceberg/${doc._id}/pcns/${pcnId}`, { method: 'DELETE' }, getToken); onUpdated(); }
    catch (_) {} finally { setSavingPcn(null); }
  };

  const patchPcn = async (pcnId, patch) => {
    setSavingPcn(pcnId);
    try { await apiFetch(`/iceberg/${doc._id}/pcns/${pcnId}`, { method: 'PATCH', body: JSON.stringify(patch) }, getToken); onUpdated(); }
    catch (_) {} finally { setSavingPcn(null); }
  };

  const addDocLink = async () => {
    const num = newDocNum.trim().toUpperCase();
    if (!num) return;
    setSavingDoc(true);
    try {
      const current = doc.linkedDocumentNumbers || [];
      if (!current.includes(num)) {
        await apiFetch(`/iceberg/${doc._id}`, { method: 'PATCH', body: JSON.stringify({ linkedDocumentNumbers: [...current, num] }) }, getToken);
        onUpdated();
      }
      setNewDocNum('');
    } catch (_) {} finally { setSavingDoc(false); }
  };

  const removeDocLink = async (num) => {
    try {
      const current = (doc.linkedDocumentNumbers || []).filter(d => d !== num);
      await apiFetch(`/iceberg/${doc._id}`, { method: 'PATCH', body: JSON.stringify({ linkedDocumentNumbers: current }) }, getToken);
      onUpdated();
    } catch (_) {}
  };

  const ecoStatusColor = { draft: '#9ca3af', submitted: '#F6AD55', approved: '#63B3ED', implemented: '#46BB89' };
  const pcnStatusColor  = { draft: '#9ca3af', sent: '#F6AD55', acknowledged: '#46BB89' };

  return (
    <div className="ib-tier-panel">
      <div className="ib-tier-panel-head" style={{ borderLeftColor: '#63B3ED' }}>
        <span style={{ color: '#63B3ED' }}>D4/D5/D6 — Root Cause Confirmed &amp; Corrective Actions</span>
        <span className="ib-tier-panel-iso">ISO 9001:2015 cl. 10.2.1c–e</span>
      </div>

      <label className="ib-form-label">
        Root cause confirmed <span className="ib-req-star">*</span>
        {form.rootCauseConfirmed?.trim() && <span className="ib-field-ok"> ✓</span>}
        <textarea className="ib-modal-textarea" rows={3} value={form.rootCauseConfirmed}
          placeholder="State the confirmed root cause. Reference the investigation evidence."
          onChange={e => setForm(d => ({ ...d, rootCauseConfirmed: e.target.value }))} />
      </label>

      <label className="ib-form-label">
        Corrective action implementation summary
        <textarea className="ib-modal-textarea" rows={3} value={form.correctiveActionSummary}
          placeholder="Summarise what was changed, implemented, or deployed as the permanent corrective action."
          onChange={e => setForm(d => ({ ...d, correctiveActionSummary: e.target.value }))} />
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4, marginBottom: 16 }}>
        <button className="ib-btn ib-btn--primary" onClick={saveMain} disabled={savingMain}>
          {savingMain ? 'Saving…' : savedMain ? '✓ Saved' : 'Save'}
        </button>
      </div>

      {/* ECO references */}
      <div className="ib-ref-section">
        <div className="ib-ref-head">
          <span style={{ color: '#B794F4' }}>Engineering Change Orders (ECO) <span className="ib-req-star">*one of these three</span></span>
          <button className="ib-btn ib-btn--muted" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setAddingEco(v => !v)}>
            {addingEco ? 'Cancel' : '+ Add ECO'}
          </button>
        </div>
        {(doc.ecoRefs || []).map(eco => (
          <div key={eco._id} className="ib-ref-row">
            <span className="ib-ref-badge" style={{ background: `${ecoStatusColor[eco.status]}18`, borderColor: `${ecoStatusColor[eco.status]}40`, color: ecoStatusColor[eco.status] }}>
              {eco.status}
            </span>
            <span className="ib-ref-number">{eco.number}</span>
            {eco.title && <span className="ib-ref-title">{eco.title}</span>}
            {eco.date && <span className="ib-ref-date">{new Date(eco.date).toLocaleDateString('en-GB')}</span>}
            <select className="ib-ref-status-sel" value={eco.status} disabled={savingEco === eco._id}
              onChange={e => patchEco(eco._id, { status: e.target.value })}>
              {ECO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="ib-btn ib-btn--close" style={{ padding: '2px 6px', fontSize: '0.7rem' }}
              disabled={savingEco === eco._id + '-del'} onClick={() => removeEco(eco._id)}>✕</button>
          </div>
        ))}
        {addingEco && (
          <div className="ib-ref-add-form">
            <input className="ib-form-input" placeholder="ECO number *" value={newEco.number} onChange={e => setNewEco(d => ({ ...d, number: e.target.value }))} />
            <input className="ib-form-input" placeholder="Title" value={newEco.title} onChange={e => setNewEco(d => ({ ...d, title: e.target.value }))} />
            <input className="ib-form-input" type="date" value={newEco.date} onChange={e => setNewEco(d => ({ ...d, date: e.target.value }))} />
            <select className="ib-form-input" value={newEco.status} onChange={e => setNewEco(d => ({ ...d, status: e.target.value }))}>
              {ECO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="ib-btn ib-btn--primary" disabled={!newEco.number.trim() || savingEco === 'new'} onClick={addEco}>Add ECO</button>
          </div>
        )}
      </div>

      {/* PCN references */}
      <div className="ib-ref-section">
        <div className="ib-ref-head">
          <span style={{ color: '#F687B3' }}>Product Change Notifications (PCN)</span>
          <button className="ib-btn ib-btn--muted" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setAddingPcn(v => !v)}>
            {addingPcn ? 'Cancel' : '+ Add PCN'}
          </button>
        </div>
        {(doc.pcnRefs || []).map(pcn => (
          <div key={pcn._id} className="ib-ref-row">
            <span className="ib-ref-badge" style={{ background: `${pcnStatusColor[pcn.status]}18`, borderColor: `${pcnStatusColor[pcn.status]}40`, color: pcnStatusColor[pcn.status] }}>
              {pcn.status}
            </span>
            <span className="ib-ref-number">{pcn.number}</span>
            {pcn.title && <span className="ib-ref-title">{pcn.title}</span>}
            {pcn.sentDate && <span className="ib-ref-date">Sent: {new Date(pcn.sentDate).toLocaleDateString('en-GB')}</span>}
            {pcn.customersNotified > 0 && <span className="ib-ref-date">{pcn.customersNotified} customers</span>}
            <select className="ib-ref-status-sel" value={pcn.status} disabled={savingPcn === pcn._id}
              onChange={e => patchPcn(pcn._id, { status: e.target.value })}>
              {PCN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="ib-btn ib-btn--close" style={{ padding: '2px 6px', fontSize: '0.7rem' }}
              disabled={savingPcn === pcn._id + '-del'} onClick={() => removePcn(pcn._id)}>✕</button>
          </div>
        ))}
        {addingPcn && (
          <div className="ib-ref-add-form">
            <input className="ib-form-input" placeholder="PCN number *" value={newPcn.number} onChange={e => setNewPcn(d => ({ ...d, number: e.target.value }))} />
            <input className="ib-form-input" placeholder="Title" value={newPcn.title} onChange={e => setNewPcn(d => ({ ...d, title: e.target.value }))} />
            <input className="ib-form-input" type="date" placeholder="Sent date" value={newPcn.sentDate} onChange={e => setNewPcn(d => ({ ...d, sentDate: e.target.value }))} />
            <input className="ib-form-input" type="number" placeholder="Customers notified" value={newPcn.customersNotified} onChange={e => setNewPcn(d => ({ ...d, customersNotified: e.target.value }))} />
            <select className="ib-form-input" value={newPcn.status} onChange={e => setNewPcn(d => ({ ...d, status: e.target.value }))}>
              {PCN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="ib-btn ib-btn--primary" disabled={!newPcn.number.trim() || savingPcn === 'new'} onClick={addPcn}>Add PCN</button>
          </div>
        )}
      </div>

      {/* Quality procedure links */}
      <div className="ib-ref-section">
        <div className="ib-ref-head">
          <span style={{ color: '#46BB89' }}>Linked Quality Documents / Procedures</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input className="ib-form-input" style={{ flex: 1 }} placeholder="Document number (e.g. QP-SRV-001)"
            value={newDocNum} onChange={e => setNewDocNum(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addDocLink()} />
          <button className="ib-btn ib-btn--muted" style={{ fontSize: '0.75rem' }} disabled={!newDocNum.trim() || savingDoc} onClick={addDocLink}>
            Link
          </button>
        </div>
        <div className="ib-docnums-list">
          {(doc.linkedDocumentNumbers || []).map(num => (
            <div key={num} className="ib-ref-row">
              <span className="ib-ref-number" style={{ color: '#46BB89' }}>📄 {num}</span>
              <button className="ib-btn ib-btn--close" style={{ padding: '2px 6px', fontSize: '0.7rem', marginLeft: 'auto' }}
                onClick={() => removeDocLink(num)}>✕</button>
            </div>
          ))}
          {(doc.linkedDocumentNumbers || []).length === 0 && (
            <div style={{ fontSize: '0.78rem', color: '#6e7681' }}>No procedures linked yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tier 4 panel — prevention, effectiveness, management sign-off ──────────────
function Tier4Panel({ doc, getToken, onUpdated }) {
  const mr = doc.managementReview || {};
  const [form, setForm] = useState({
    preventiveAction:          doc.preventiveAction          || '',
    effectivenessVerification: doc.effectivenessVerification || '',
    effectivenessTarget:       doc.effectivenessTarget       || '',
    reviewedBy:  mr.reviewedBy  || '',
    reviewedAt:  mr.reviewedAt  ? new Date(mr.reviewedAt).toISOString().slice(0, 10) : '',
    reviewNotes: mr.notes       || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/iceberg/${doc._id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          preventiveAction:          form.preventiveAction,
          effectivenessVerification: form.effectivenessVerification,
          effectivenessTarget:       form.effectivenessTarget,
          managementReview: {
            reviewedBy: form.reviewedBy,
            reviewedAt: form.reviewedAt || null,
            notes:      form.reviewNotes,
          },
        }),
      }, getToken);
      setSaved(true); setTimeout(() => setSaved(false), 2000); onUpdated();
    } catch (_) {} finally { setSaving(false); }
  };

  return (
    <div className="ib-tier-panel">
      <div className="ib-tier-panel-head" style={{ borderLeftColor: '#46BB89' }}>
        <span style={{ color: '#46BB89' }}>D7/D8 — Prevention, Verification &amp; Sign-off</span>
        <span className="ib-tier-panel-iso">ISO 9001:2015 cl. 10.2.1f–g</span>
      </div>

      <label className="ib-form-label">
        Preventive action taken <span className="ib-req-star">*</span>
        {form.preventiveAction?.trim() && <span className="ib-field-ok"> ✓</span>}
        <textarea className="ib-modal-textarea" rows={3} value={form.preventiveAction}
          placeholder="What systemic change was made to prevent recurrence? Procedure updated, design changed, training delivered…"
          onChange={e => setForm(d => ({ ...d, preventiveAction: e.target.value }))} />
      </label>

      <label className="ib-form-label">
        Effectiveness verification plan <span className="ib-req-star">*</span>
        {form.effectivenessVerification?.trim() && <span className="ib-field-ok"> ✓</span>}
        <textarea className="ib-modal-textarea" rows={3} value={form.effectivenessVerification}
          placeholder="How and when will we verify that the corrective action was effective? Who is responsible?"
          onChange={e => setForm(d => ({ ...d, effectivenessVerification: e.target.value }))} />
      </label>

      <label className="ib-form-label">
        Measurable KPI / target
        <input className="ib-form-input" value={form.effectivenessTarget}
          placeholder="e.g. Zero recurrences of this failure mode in 6 months, warranty rate below 0.2%"
          onChange={e => setForm(d => ({ ...d, effectivenessTarget: e.target.value }))} />
      </label>

      <div className="ib-mgmt-review-box">
        <div className="ib-mgmt-review-title">Management Review Sign-off <span className="ib-req-star">*</span></div>
        <div className="ib-mgmt-review-grid">
          <label className="ib-form-label">
            Reviewed by <span className="ib-req-star">*</span>
            {form.reviewedBy?.trim() && <span className="ib-field-ok"> ✓</span>}
            <input className="ib-form-input" value={form.reviewedBy}
              placeholder="Name / title of approver"
              onChange={e => setForm(d => ({ ...d, reviewedBy: e.target.value }))} />
          </label>
          <label className="ib-form-label">
            Date
            <input className="ib-form-input" type="date" value={form.reviewedAt}
              onChange={e => setForm(d => ({ ...d, reviewedAt: e.target.value }))} />
          </label>
        </div>
        <label className="ib-form-label">
          Review notes
          <textarea className="ib-modal-textarea" rows={2} value={form.reviewNotes}
            placeholder="Any conditions, exceptions, or follow-up requirements from management…"
            onChange={e => setForm(d => ({ ...d, reviewNotes: e.target.value }))} />
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="ib-btn ib-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save & sign'}
        </button>
      </div>
    </div>
  );
}

// ── Evidence panel ────────────────────────────────────────────────────────────
function EvidencePanel({ doc, getToken, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm]     = useState({ title: '', url: '', type: 'other', notes: '' });
  const [saving, setSaving] = useState(null);

  const add = async () => {
    if (!form.title.trim()) return;
    setSaving('new');
    try {
      await apiFetch(`/iceberg/${doc._id}/evidence`, { method: 'POST', body: JSON.stringify(form) }, getToken);
      setForm({ title: '', url: '', type: 'other', notes: '' });
      setAdding(false); onUpdated();
    } catch (_) {} finally { setSaving(null); }
  };

  const remove = async (id) => {
    setSaving(id);
    try { await apiFetch(`/iceberg/${doc._id}/evidence/${id}`, { method: 'DELETE' }, getToken); onUpdated(); }
    catch (_) {} finally { setSaving(null); }
  };

  const typeIcon = { photo:'📸', report:'📋', lab_test:'🔬', email:'✉️', drawing:'📐', specification:'📄', procedure_update:'📝', measurement:'📏', other:'📎' };

  return (
    <div className="ib-evidence-panel">
      <div className="ib-modal-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Evidence &amp; Documents</span>
        <button className="ib-btn ib-btn--muted" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setAdding(v => !v)}>
          {adding ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {adding && (
        <div className="ib-ref-add-form">
          <input className="ib-form-input" placeholder="Title / description *" value={form.title}
            onChange={e => setForm(d => ({ ...d, title: e.target.value }))} />
          <select className="ib-form-input" value={form.type} onChange={e => setForm(d => ({ ...d, type: e.target.value }))}>
            {EVIDENCE_TYPES.map(t => <option key={t} value={t}>{typeIcon[t]} {t.replace('_', ' ')}</option>)}
          </select>
          <input className="ib-form-input" placeholder="URL (SharePoint, OneDrive, etc.)" value={form.url}
            onChange={e => setForm(d => ({ ...d, url: e.target.value }))} />
          <input className="ib-form-input" placeholder="Notes (optional)" value={form.notes}
            onChange={e => setForm(d => ({ ...d, notes: e.target.value }))} />
          <button className="ib-btn ib-btn--primary" disabled={!form.title.trim() || saving === 'new'} onClick={add}>
            Attach evidence
          </button>
        </div>
      )}

      {(doc.evidence || []).length === 0 && !adding && (
        <div className="ib-evidence-empty">No evidence attached yet — attach photos, reports, test results, emails…</div>
      )}
      {(doc.evidence || []).length > 0 && (
        <div className="ib-evidence-list">
          {(doc.evidence || []).map(ev => (
            <div key={ev._id} className="ib-evidence-row">
              <span className="ib-evidence-icon">{typeIcon[ev.type] || '📎'}</span>
              <div className="ib-evidence-info">
                {ev.url ? (
                  <a className="ib-evidence-title" href={ev.url} target="_blank" rel="noreferrer">{ev.title}</a>
                ) : (
                  <span className="ib-evidence-title">{ev.title}</span>
                )}
                <span className="ib-evidence-meta">
                  {ev.type.replace('_', ' ')}
                  {ev.notes && ` · ${ev.notes}`}
                  {ev.addedAt && ` · ${new Date(ev.addedAt).toLocaleDateString('en-GB')}`}
                </span>
              </div>
              <button className="ib-btn ib-btn--close" style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                disabled={saving === ev._id} onClick={() => remove(ev._id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Actions panel ─────────────────────────────────────────────────────────────
function ActionsPanel({ doc, getToken, onUpdated }) {
  const [editing, setEditing]   = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving]     = useState(null);
  const [adding, setAdding]     = useState(false);
  const [newAction, setNewAction] = useState({ text: '', area: '', person: '', dueDate: '' });

  const adopt = async (item) => {
    setSaving('adopt-' + item.text.slice(0, 10));
    try {
      await apiFetch(`/iceberg/${doc._id}/actions`, {
        method: 'POST',
        body: JSON.stringify({ text: item.text, area: item.suggestedArea || '', aiSuggestedArea: item.suggestedArea || '', areaReasoning: item.areaReasoning || '' }),
      }, getToken);
      onUpdated();
    } catch (_) {} finally { setSaving(null); }
  };

  const saveEdit = async (actionId) => {
    setSaving(actionId);
    try { await apiFetch(`/iceberg/${doc._id}/actions/${actionId}`, { method: 'PATCH', body: JSON.stringify(editData) }, getToken); setEditing(null); onUpdated(); }
    catch (_) {} finally { setSaving(null); }
  };

  const deleteAction = async (actionId) => {
    setSaving(actionId);
    try { await apiFetch(`/iceberg/${doc._id}/actions/${actionId}`, { method: 'DELETE' }, getToken); onUpdated(); }
    catch (_) {} finally { setSaving(null); }
  };

  const createManual = async () => {
    if (!newAction.text.trim()) return;
    setSaving('new');
    try {
      await apiFetch(`/iceberg/${doc._id}/actions`, { method: 'POST', body: JSON.stringify(newAction) }, getToken);
      setNewAction({ text: '', area: '', person: '', dueDate: '' }); setAdding(false); onUpdated();
    } catch (_) {} finally { setSaving(null); }
  };

  const cycleStatus = async (action) => {
    const next = { open: 'in-progress', 'in-progress': 'done', done: 'open' };
    setSaving(action._id);
    try { await apiFetch(`/iceberg/${doc._id}/actions/${action._id}`, { method: 'PATCH', body: JSON.stringify({ status: next[action.status] }) }, getToken); onUpdated(); }
    catch (_) {} finally { setSaving(null); }
  };

  const aiItems      = doc.aiAnalysis?.actionItems || [];
  const ownedActions = doc.actions || [];
  const adoptedTexts = new Set(ownedActions.map(a => a.text));
  const statusIcon   = { open: '○', 'in-progress': '◑', done: '●' };

  return (
    <div className="ib-actions-panel">
      <div className="ib-modal-field-label">Corrective Action Items</div>

      {aiItems.length > 0 && (
        <div className="ib-ai-suggestions">
          <div className="ib-ai-suggestions-head">◈ AI suggestions — click Adopt to take ownership</div>
          {aiItems.map((item, i) => {
            const adopted = adoptedTexts.has(item.text);
            const areaColor = AREA_COLORS[item.suggestedArea] || '#8b949e';
            return (
              <div key={i} className={`ib-ai-suggestion-row ${adopted ? 'ib-ai-suggestion-row--done' : ''}`}>
                <div className="ib-ai-suggestion-body">
                  <span className="ib-ai-suggestion-text">{item.text}</span>
                  {item.suggestedArea && (
                    <span className="ib-area-badge" style={{ background: `${areaColor}18`, borderColor: `${areaColor}40`, color: areaColor }}>{item.suggestedArea}</span>
                  )}
                  {item.areaReasoning && <span className="ib-ai-suggestion-reason">{item.areaReasoning}</span>}
                </div>
                <button className="ib-btn ib-btn--primary" style={{ fontSize: '0.72rem', padding: '3px 10px', flexShrink: 0 }}
                  disabled={adopted || saving === 'adopt-' + item.text.slice(0, 10)} onClick={() => adopt(item)}>
                  {adopted ? '✓ Adopted' : 'Adopt →'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {ownedActions.length > 0 && (
        <div className="ib-owned-actions">
          {ownedActions.map(action => {
            const isEditing  = editing === action._id;
            const areaToShow = action.area || action.aiSuggestedArea;
            const areaColor  = AREA_COLORS[areaToShow] || '#8b949e';
            const statusColor = { open: '#6e7681', 'in-progress': '#F6AD55', done: '#46BB89' }[action.status];
            return (
              <div key={action._id} className="ib-owned-action">
                {!isEditing ? (
                  <>
                    <button className="ib-status-cycle" style={{ color: statusColor }} onClick={() => cycleStatus(action)} title="Click to advance status">
                      {statusIcon[action.status]}
                    </button>
                    <div className="ib-owned-action-body">
                      <span className="ib-owned-action-text" style={{ textDecoration: action.status === 'done' ? 'line-through' : 'none', opacity: action.status === 'done' ? 0.55 : 1 }}>
                        {action.text}
                      </span>
                      {action.completionNote && (
                        <span className="ib-owned-action-completion-note">
                          {action.status === 'done' ? '✓ ' : '↗ '}{action.completionNote}
                        </span>
                      )}
                      <div className="ib-owned-action-meta">
                        {areaToShow && (
                          <span className="ib-area-badge" style={{ background: `${areaColor}18`, borderColor: `${areaColor}40`, color: areaColor }}>
                            {areaToShow}{!action.area && action.aiSuggestedArea && <span style={{ opacity: 0.6 }}> ◈</span>}
                          </span>
                        )}
                        {action.person && <span className="ib-owned-action-person">👤 {action.person}</span>}
                        {action.dueDate && <span className="ib-owned-action-due">📅 {new Date(action.dueDate).toLocaleDateString('en-GB')}</span>}
                      </div>
                    </div>
                    <div className="ib-owned-action-btns">
                      <button className="ib-btn ib-btn--muted" style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                        onClick={() => { setEditing(action._id); setEditData({ area: action.area || '', person: action.person || '', dueDate: action.dueDate ? action.dueDate.slice(0, 10) : '', status: action.status, completionNote: action.completionNote || '' }); }}>
                        Edit
                      </button>
                      <button className="ib-btn ib-btn--close" style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                        disabled={saving === action._id} onClick={() => deleteAction(action._id)}>✕</button>
                    </div>
                  </>
                ) : (
                  <div className="ib-action-edit-form">
                    <select className="ib-form-input" style={{ flex: 1 }} value={editData.area} onChange={e => setEditData(d => ({ ...d, area: e.target.value }))}>
                      <option value="">Area</option>
                      {AREAS.map(a => <option key={a.code} value={a.code}>{a.code} — {a.label}</option>)}
                    </select>
                    <input className="ib-form-input" style={{ flex: 1 }} placeholder="Person responsible" value={editData.person} onChange={e => setEditData(d => ({ ...d, person: e.target.value }))} />
                    <input className="ib-form-input" type="date" style={{ flex: 1 }} value={editData.dueDate} onChange={e => setEditData(d => ({ ...d, dueDate: e.target.value }))} />
                    <textarea className="ib-modal-textarea" style={{ fontSize: '0.8rem', minHeight: 48 }}
                      placeholder="Completion / escalation note (how was this resolved or handed off?)"
                      value={editData.completionNote} onChange={e => setEditData(d => ({ ...d, completionNote: e.target.value }))} rows={2} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="ib-btn ib-btn--primary" disabled={saving === action._id} onClick={() => saveEdit(action._id)}>Save</button>
                      <button className="ib-btn ib-btn--muted" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {adding ? (
        <div className="ib-action-edit-form">
          <input className="ib-form-input" placeholder="Action description *" value={newAction.text} onChange={e => setNewAction(d => ({ ...d, text: e.target.value }))} />
          <select className="ib-form-input" value={newAction.area} onChange={e => setNewAction(d => ({ ...d, area: e.target.value }))}>
            <option value="">Responsible area</option>
            {AREAS.map(a => <option key={a.code} value={a.code}>{a.code} — {a.label}</option>)}
          </select>
          <input className="ib-form-input" placeholder="Person responsible" value={newAction.person} onChange={e => setNewAction(d => ({ ...d, person: e.target.value }))} />
          <input className="ib-form-input" type="date" value={newAction.dueDate} onChange={e => setNewAction(d => ({ ...d, dueDate: e.target.value }))} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="ib-btn ib-btn--primary" disabled={!newAction.text.trim() || saving === 'new'} onClick={createManual}>Add</button>
            <button className="ib-btn ib-btn--muted" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="ib-btn ib-btn--muted" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={() => setAdding(true)}>+ Add action</button>
      )}
    </div>
  );
}

// ── RMA link panel ────────────────────────────────────────────────────────────
function RmaLinkPanel({ doc, getToken, onUpdated }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [linking, setLinking]     = useState(null);
  const [unlinking, setUnlinking] = useState(null);

  const search = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try { const res = await apiFetch(`/iceberg/rma-search?q=${encodeURIComponent(q.trim())}`, {}, getToken); setResults(res.results || []); }
    catch (_) { setResults([]); } finally { setSearching(false); }
  };

  const link = async (rmaId) => {
    setLinking(rmaId);
    try { await apiFetch(`/iceberg/${doc._id}/link-rma`, { method: 'POST', body: JSON.stringify({ rmaId }) }, getToken); onUpdated(); setResults(r => r.filter(x => x.rmaId !== rmaId)); }
    catch (_) {} finally { setLinking(null); }
  };

  const unlink = async (rmaId) => {
    setUnlinking(rmaId);
    try { await apiFetch(`/iceberg/${doc._id}/link-rma/${encodeURIComponent(rmaId)}`, { method: 'DELETE' }, getToken); onUpdated(); }
    catch (_) {} finally { setUnlinking(null); }
  };

  const loadByProduct = async () => {
    const pn = doc.productNumbers?.[0];
    if (!pn) return;
    setLoadingBulk(true);
    try { const res = await apiFetch(`/iceberg/rma-by-product?pn=${encodeURIComponent(pn)}`, {}, getToken); setResults(res.results || []); }
    catch (_) {} finally { setLoadingBulk(false); }
  };

  const linkedIds = new Set(doc.linkedRmaIds || []);
  return (
    <div className="ib-rma-panel">
      <div className="ib-modal-field-label">Linked RMA Evidence</div>
      {linkedIds.size > 0 && (
        <div className="ib-rma-linked-list">
          {Array.from(linkedIds).map(id => (
            <div key={id} className="ib-rma-linked-row">
              <span className="ib-rma-id">{id}</span>
              <button className="ib-btn ib-btn--close" style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={() => unlink(id)} disabled={unlinking === id}>{unlinking === id ? '…' : '✕'}</button>
            </div>
          ))}
        </div>
      )}
      <div className="ib-rma-search-wrap">
        <input className="ib-form-input" placeholder="Search by RMA ID or serial number…" value={query} onChange={e => search(e.target.value)} />
        {searching && <span className="ib-rma-searching">Searching…</span>}
      </div>
      {doc.productNumbers?.length > 0 && (
        <button className="ib-btn ib-btn--muted" style={{ alignSelf: 'flex-start', fontSize: '0.75rem' }} onClick={loadByProduct} disabled={loadingBulk}>
          {loadingBulk ? 'Loading…' : `⊞ Load all RMAs for ${doc.productNumbers[0]}`}
        </button>
      )}
      {results.length > 0 && (
        <div className="ib-rma-results">
          {results.map(r => {
            const already = linkedIds.has(r.rmaId);
            return (
              <div key={r.rmaId} className="ib-rma-result-row">
                <div className="ib-rma-result-info">
                  <span className="ib-rma-id">{r.rmaId}</span>
                  {r.serialNumber && <span className="ib-rma-serial">SN: {r.serialNumber}</span>}
                  {r.productNumber && <span className="ib-rma-pn">{r.productNumber}</span>}
                  {(r.rootCause || r.categories?.length > 0) && <span className="ib-rma-fault">{[r.rootCause, ...(r.categories || [])].filter(Boolean).join(' · ')}</span>}
                  {r.notes && <span className="ib-rma-notes">{r.notes}</span>}
                </div>
                <button className="ib-btn ib-btn--primary" style={{ flexShrink: 0, fontSize: '0.75rem', padding: '4px 10px' }}
                  onClick={() => link(r.rmaId)} disabled={already || linking === r.rmaId}>
                  {already ? '✓ Linked' : linking === r.rmaId ? '…' : '+ Link'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Case detail modal ─────────────────────────────────────────────────────────
function CaseModal({ doc, getToken, onClose, onUpdated }) {
  const [note, setNote]     = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab]       = useState('documentation');

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await apiFetch(`/iceberg/${doc._id}/notes`, { method: 'POST', body: JSON.stringify({ text: note }) }, getToken); setNote(''); onUpdated(); }
    catch (_) {} finally { setSaving(false); }
  };

  const tier     = TIERS.find(t => t.id === doc.tier) || TIERS[0];
  const ai       = doc.aiAnalysis || {};
  const gateErrs = computeGate(doc);
  const closeErrs = computeCloseGate(doc);

  const tabs = [
    { id: 'documentation', label: `${tier.number}. ${tier.label} Docs` },
    { id: 'evidence',      label: `📎 Evidence (${doc.evidence?.length || 0})` },
    { id: 'rma',           label: `RMA (${doc.linkedRmaIds?.length || 0})` },
    { id: 'actions',       label: `Actions (${doc.actions?.length || 0})` },
    { id: 'ai',            label: '◈ AI Analysis' },
    { id: 'notes',         label: 'Notes' },
  ];

  return (
    <div className="ib-modal-overlay" onClick={onClose}>
      <div className="ib-modal ib-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ib-modal-header" style={{ borderBottomColor: `${tier.color}40` }}>
          <div>
            <span className="ib-modal-tier" style={{ color: tier.color }}>{tier.label}</span>
            <h2 className="ib-modal-title">{doc.title}</h2>
            {doc.productNumbers?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {doc.productNumbers.map(pn => (
                  <span key={pn} className="ib-card-tag" style={{ color: tier.color, borderColor: `${tier.color}40`, background: `${tier.color}0d` }}>{pn}</span>
                ))}
              </div>
            )}
          </div>
          <button className="ib-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Gate status bar */}
        {(gateErrs.length > 0 || closeErrs.length > 0) && (
          <div className="ib-modal-gate-bar">
            <span className="ib-modal-gate-icon" style={{ color: tier.color }}>○</span>
            <span className="ib-modal-gate-label">
              {[...gateErrs, ...closeErrs].length} requirement{[...gateErrs, ...closeErrs].length > 1 ? 's' : ''} pending before advancing:&nbsp;
              {[...gateErrs, ...closeErrs].join(' · ')}
            </span>
          </div>
        )}
        {gateErrs.length === 0 && closeErrs.length === 0 && (
          <div className="ib-modal-gate-bar ib-modal-gate-bar--ok">
            <span style={{ color: '#46BB89' }}>✓</span> All requirements met
          </div>
        )}

        {/* Tab bar */}
        <div className="ib-modal-tabs">
          {tabs.map(t => (
            <button key={t.id} className={`ib-modal-tab ${tab === t.id ? 'ib-modal-tab--active' : ''}`}
              style={{ '--tab-color': tier.color }} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="ib-modal-body">
          {doc.description && tab === 'documentation' && (
            <p className="ib-modal-desc">{doc.description}</p>
          )}

          {tab === 'documentation' && doc.tier === 'event'        && <Tier1Panel doc={doc} getToken={getToken} onUpdated={onUpdated} />}
          {tab === 'documentation' && doc.tier === 'pattern'      && <Tier2Panel doc={doc} getToken={getToken} onUpdated={onUpdated} />}
          {tab === 'documentation' && doc.tier === 'structure'    && <Tier3Panel doc={doc} getToken={getToken} onUpdated={onUpdated} />}
          {tab === 'documentation' && doc.tier === 'mental-model' && <Tier4Panel doc={doc} getToken={getToken} onUpdated={onUpdated} />}

          {tab === 'evidence' && <EvidencePanel doc={doc} getToken={getToken} onUpdated={onUpdated} />}
          {tab === 'rma'      && <RmaLinkPanel  doc={doc} getToken={getToken} onUpdated={onUpdated} />}
          {tab === 'actions'  && <ActionsPanel  doc={doc} getToken={getToken} onUpdated={onUpdated} />}

          {tab === 'ai' && (
            <div>
              {(ai.tier1_event || ai.tier2_pattern || ai.tier3_structure || ai.tier4_mentalModel) ? (
                <div className="ib-modal-iceberg">
                  <div className="ib-modal-iceberg-head">Iceberg Analysis</div>
                  {ai.tier1_event && <div className="ib-modal-tier-block" style={{ borderLeftColor: '#FC8181' }}><span className="ib-modal-tier-label" style={{ color: '#FC8181' }}>Tier 1 – Event</span><p>{ai.tier1_event}</p></div>}
                  {ai.tier2_pattern && <div className="ib-modal-tier-block" style={{ borderLeftColor: '#F6AD55' }}><span className="ib-modal-tier-label" style={{ color: '#F6AD55' }}>Tier 2 – Pattern</span><p>{ai.tier2_pattern}</p></div>}
                  {ai.tier3_structure && <div className="ib-modal-tier-block" style={{ borderLeftColor: '#63B3ED' }}><span className="ib-modal-tier-label" style={{ color: '#63B3ED' }}>Tier 3 – System Structure</span><p>{ai.tier3_structure}</p></div>}
                  {ai.tier4_mentalModel && <div className="ib-modal-tier-block" style={{ borderLeftColor: '#46BB89' }}><span className="ib-modal-tier-label" style={{ color: '#46BB89' }}>Tier 4 – Mental Model</span><p>{ai.tier4_mentalModel}</p></div>}
                  {ai.suggestPromotion && ai.promotionReason && (
                    <div className="ib-ai-promote-hint">
                      <span style={{ color: '#63B3ED' }}>◈ AI recommends advancing:</span> {ai.promotionReason}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '20px 0', color: '#6e7681', fontSize: '0.85rem' }}>
                  No AI analysis yet. Run ◈ Analyse from the case card to get tier-specific insights.
                </div>
              )}
            </div>
          )}

          {tab === 'notes' && (
            <div>
              {doc.notes?.length > 0 && (
                <div className="ib-modal-notes">
                  {doc.notes.map((n, i) => (
                    <div key={i} className="ib-modal-note">
                      <span className="ib-modal-note-date">{new Date(n.createdAt).toLocaleDateString('en-GB')}</span>
                      <span>{n.text}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="ib-modal-add-note">
                <textarea className="ib-modal-textarea" value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note…" rows={3} />
                <button className="ib-btn ib-btn--primary" onClick={addNote} disabled={saving || !note.trim()}>{saving ? 'Saving…' : 'Add note'}</button>
              </div>

              <button className="ib-btn ib-btn--pdf" style={{ marginTop: 16 }}
                onClick={async () => {
                  try {
                    const token = await getToken();
                    const r = await fetch(`${API_BASE}/iceberg/${doc._id}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                    if (!r.ok) return;
                    const blob = await r.blob();
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement('a'); a.href = url; a.download = `iceberg-${doc._id}.pdf`; a.click();
                    URL.revokeObjectURL(url);
                  } catch (_) {}
                }}>
                ↓ Download PDF Report
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── New case form ─────────────────────────────────────────────────────────────
function NewCaseModal({ onClose, onCreated, getToken }) {
  const [title, setTitle] = useState('');
  const [desc,  setDesc]  = useState('');
  const [pns,   setPns]   = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true); setErr(null);
    try {
      const productNumbers = pns.split(',').map(s => s.trim()).filter(Boolean);
      const res = await apiFetch('/iceberg', { method: 'POST', body: JSON.stringify({ title: title.trim(), description: desc.trim(), productNumbers }) }, getToken);
      onCreated(res.case);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="ib-modal-overlay" onClick={onClose}>
      <div className="ib-modal ib-modal--narrow" onClick={e => e.stopPropagation()}>
        <div className="ib-modal-header">
          <h2 className="ib-modal-title">New Iceberg Case</h2>
          <button className="ib-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="ib-modal-body" onSubmit={handleSubmit}>
          <label className="ib-form-label">Title * <input className="ib-form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Short description of the quality event" required /></label>
          <label className="ib-form-label">Description <textarea className="ib-modal-textarea" value={desc} onChange={e => setDesc(e.target.value)} placeholder="What happened?" rows={3} /></label>
          <label className="ib-form-label">Product numbers (comma-separated) <input className="ib-form-input" value={pns} onChange={e => setPns(e.target.value)} placeholder="WE270 001 087, WE240 000 105" /></label>
          {err && <div className="ib-form-error">{err}</div>}
          <div className="ib-form-actions">
            <button type="button" className="ib-btn ib-btn--muted" onClick={onClose}>Cancel</button>
            <button type="submit" className="ib-btn ib-btn--primary" disabled={saving || !title.trim()}>{saving ? 'Creating…' : 'Create case'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Group events modal ────────────────────────────────────────────────────────
function GroupModal({ selectedIds, cases, getToken, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [desc,  setDesc]  = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  const selectedCases = cases.filter(c => selectedIds.has(c._id));
  const allPns = [...new Set(selectedCases.flatMap(c => c.productNumbers || []))];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true); setErr(null);
    try {
      await apiFetch('/iceberg/group', { method: 'POST', body: JSON.stringify({ childCaseIds: [...selectedIds], title: title.trim(), description: desc.trim() }) }, getToken);
      onCreated();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="ib-modal-overlay" onClick={onClose}>
      <div className="ib-modal ib-modal--narrow" onClick={e => e.stopPropagation()}>
        <div className="ib-modal-header">
          <h2 className="ib-modal-title">Group {selectedIds.size} Events → Pattern</h2>
          <button className="ib-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="ib-modal-body" onSubmit={handleSubmit}>
          <div style={{ fontSize: '0.82rem', color: '#8b949e' }}>
            Grouping these events:
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#c9d1d9', fontSize: '0.82rem' }}>
              {selectedCases.map(c => <li key={c._id}>{c.title}</li>)}
            </ul>
          </div>
          {allPns.length > 0 && <div style={{ fontSize: '0.78rem', color: '#8b949e' }}>Products: <span style={{ color: '#F6AD55' }}>{allPns.join(', ')}</span></div>}
          <label className="ib-form-label">Pattern title * <input className="ib-form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="What recurring pattern do these events share?" required /></label>
          <label className="ib-form-label">Description <textarea className="ib-modal-textarea" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe the pattern…" rows={3} /></label>
          {err && <div className="ib-form-error">{err}</div>}
          <div className="ib-form-actions">
            <button type="button" className="ib-btn ib-btn--muted" onClick={onClose}>Cancel</button>
            <button type="submit" className="ib-btn ib-btn--primary" disabled={saving || !title.trim()}>{saving ? 'Grouping…' : 'Create Pattern →'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function IcebergPage() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [cases, setCases]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [selectMode, setSelectMode]         = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [showGroup, setShowGroup]           = useState(false);

  const loadCases = useCallback(async () => {
    setLoading(true); setError(null);
    try { const res = await apiFetch('/iceberg', {}, getToken); setCases(res.cases || []); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    loadCases();
  }, [loadCases, isLoaded, isSignedIn]);

  const refreshSelected = useCallback(async () => {
    if (!selected) return;
    try { const res = await apiFetch(`/iceberg/${selected._id}`, {}, getToken); setSelected(res.case); }
    catch (_) {}
    loadCases();
  }, [selected, getToken, loadCases]);

  const filteredCases = cases.filter(c => statusFilter === 'all' ? true : c.status === statusFilter);

  return (
    <div className="ib-page">
      <div className="ib-header">
        <div className="ib-header-left">
          <h1 className="ib-page-title">Iceberg Model</h1>
          <p className="ib-page-sub">ISO 9001:2015 cl. 10.2 — Nonconformity &amp; Corrective Action · 8D Problem Solving</p>
        </div>
        <div className="ib-header-right">
          <div className="ib-filter-group">
            {['open', 'in-progress', 'resolved', 'all'].map(s => (
              <button key={s} className={`ib-filter-btn ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>{s}</button>
            ))}
          </div>
          <button className="ib-btn ib-btn--primary" onClick={() => setShowNew(true)}>+ New case</button>
        </div>
      </div>

      {loading ? (
        <div className="ib-loading">Loading cases…</div>
      ) : error ? (
        <div className="ib-error">
          <div>{error}</div>
          <button className="ib-btn ib-btn--muted" style={{ marginTop: 12 }} onClick={loadCases}>↺ Retry</button>
        </div>
      ) : (
        <div className="ib-board">
          {TIERS.map(tier => {
            const tierCases = filteredCases.filter(c => c.tier === tier.id);
            return (
              <div key={tier.id} className="ib-column" style={{ borderTopColor: tier.color }}>
                <div className="ib-col-header">
                  <div className="ib-col-number" style={{ background: tier.color }}>{tier.number}</div>
                  <div className="ib-col-info">
                    <span className="ib-col-label" style={{ color: tier.color }}>{tier.label}</span>
                    <span className="ib-col-desc">{tier.desc}</span>
                  </div>
                  <span className="ib-col-count">{tierCases.length}</span>
                  {tier.id === 'event' && (
                    <button className={`ib-btn ${selectMode ? 'ib-btn--ai' : 'ib-btn--muted'}`}
                      style={{ fontSize: '0.68rem', padding: '3px 8px' }}
                      onClick={() => { setSelectMode(s => !s); setSelectedEvents(new Set()); }}>
                      {selectMode ? '✕ Cancel' : '⊞ Group'}
                    </button>
                  )}
                </div>
                <div className="ib-col-cards">
                  {tierCases.length === 0 ? (
                    <div className="ib-col-empty">No {statusFilter !== 'all' ? statusFilter : ''} cases</div>
                  ) : (
                    tierCases.map(doc => (
                      <CaseCard key={doc._id} doc={doc} tier={tier} getToken={getToken}
                        onUpdated={loadCases} onSelect={setSelected}
                        selectMode={tier.id === 'event' ? selectMode : false}
                        selected={selectedEvents.has(doc._id)}
                        onToggleSelect={id => setSelectedEvents(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectMode && selectedEvents.size >= 2 && (
        <div className="ib-group-bar">
          <span className="ib-group-bar-text">{selectedEvents.size} events selected</span>
          <button className="ib-btn ib-btn--primary" onClick={() => setShowGroup(true)}>⊞ Group into Pattern →</button>
        </div>
      )}

      {selected && <CaseModal doc={selected} getToken={getToken} onClose={() => setSelected(null)} onUpdated={refreshSelected} />}
      {showNew   && <NewCaseModal getToken={getToken} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadCases(); }} />}
      {showGroup && (
        <GroupModal selectedIds={selectedEvents} cases={cases} getToken={getToken}
          onClose={() => setShowGroup(false)}
          onCreated={() => { setShowGroup(false); setSelectMode(false); setSelectedEvents(new Set()); loadCases(); }}
        />
      )}
    </div>
  );
}
